import { put, call, getContext, select, delay, all } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { checkServiceFail, checkServiceStart, checkServiceSuccess } from '../../../reducers/checkoutInfo';
import { checkService as checkServiceRequest, TCheckServiceResult } from '../../../../services/checkService';
import { getCheckServiceParams, getPublishTerms } from './utils';
import { IAppState } from '../../../types';
import { TActions } from '../../../../types/actions';
import {
  selectActivePackage,
  selectActivePlacingType,
  selectActiveService,
  selectPublicationPeriod,
  selectServiceById,
  selectServiceUnavailableReason,
  selectIsWithoutServices,
  selectIsIncreaseToMinimalBet,
  selectPlacingMoneySource,
} from '../../../../selectors/publishTerms';
import { selectIsAuctionSelected, selectMinimalBet } from '../../../../selectors/auction';
import { selectTermsDuration } from '../../../../selectors/termsDurations';
import { ITelemetryService } from '../../../../types/telemetryService';
import {
  changePromotionService,
  CHANGE_AUCTION_BET,
  EUnavailableReason,
  IBetPosition,
  IPublishTermsService,
  SELECT_PUBLISH_TERMS_SERVICE,
  updateAuction,
  setIsWithoutMinimalBet,
} from '../../../reducers';
import {
  selectPhotos,
  selectLocationsFromAddress,
  selectOfferCoordinatesWithFallback,
  selectDynamicPricingParameters,
} from '../../../../selectors/offer';
import { trackBetChange, trackBetTyping, trackServiceClick, trackUnavailableServiceClick } from './tracking';
import { selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../../../selectors/analytics';
import { IAnalyticsProduct, TAnalyticsOfferStatus } from '../../../../types/analytics';
import { selectBetPosition } from '../../../../selectors/betPosition';
import { IConfig } from '@cian/config/shared';

export function* checkService(checkServiceAction: TActions) {
  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const telemetryService: ITelemetryService = yield getContext('telemetryService');
  const state: IAppState = yield select();
  const config: IConfig = yield getContext('config');
  const isServicePickerWidgetEnabled = config.get<boolean>('addFormFrontend.ServicePickerWidget.isEnabled') || false;

  if (isServicePickerWidgetEnabled) {
    return;
  }

  const service = selectActiveService(state);
  const servicePackage = selectActivePackage(state);
  const publicationPeriod = selectPublicationPeriod(state);
  const isAuctionSelected = selectIsAuctionSelected(state);
  const placingType = selectActivePlacingType(state);
  const locationPath = selectLocationsFromAddress(state);
  const photos = selectPhotos(state);
  const coordinates = selectOfferCoordinatesWithFallback(state);
  const increaseToMinimalBet = selectIsIncreaseToMinimalBet(state);
  const isWithoutServices = selectIsWithoutServices(state);
  const minimalBet = selectMinimalBet(state);
  const moneySource = selectPlacingMoneySource(state);
  const termsDuration = selectTermsDuration(state);
  const extraData = selectDynamicPricingParameters(state);

  const { offer, checkoutInfo, auction, calltracking } = state;

  const isServiceChanged = checkServiceAction.type === SELECT_PUBLISH_TERMS_SERVICE;

  const terms = getPublishTerms({
    service,
    servicePackage,
    publicationPeriod,
    placingType,
    auction,
    isAuctionSelected,
    calltracking,
    increaseToMinimalBet,
    isWithoutServices,
    minimalBet,
    termsDuration,
  });

  yield put(checkServiceStart());

  const parameters = getCheckServiceParams({
    offer,
    locationPath,
    checkoutInfo,
    terms,
    isServiceChanged,
    photosCount: photos.length,
    coordinates,
    moneySource,
    extraData,
  });

  const [response]: [TCheckServiceResult] = yield all([
    call(checkServiceRequest, {
      httpApi,
      logger,
      telemetryService,
      parameters,
    }),
    // При быстром ответе сервера лоадер появляется на долю секунд в инпуте ставки, из-за чего происходит мерцание.
    // Для хорошего UX ограничиваем минимально возможное выполнение запроса
    delay(1000),
  ]);

  if (response.success) {
    const {
      selectedPromotionInfo: { serviceId },
      auction,
    } = response;

    if (isAuctionSelected) {
      yield put(updateAuction(auction));
      yield put(changePromotionService(serviceId));
    }

    yield put(setIsWithoutMinimalBet(response.payload.withoutMinimalBet));
    yield put(checkServiceSuccess(response.payload));

    const analyticsOfferStatus: TAnalyticsOfferStatus = yield select(selectAnalyticsOfferStatus);
    const analyticsProduct: IAnalyticsProduct = yield select(selectAnalyticsProduct);
    const betPosition: IBetPosition | null = yield select(selectBetPosition);
    const service: IPublishTermsService | null = yield select(selectServiceById(serviceId));

    const { offerPosition: offerSerpPosition = null, page: offerSerpPage = null } = betPosition || {};
    const { isAvailable: isServiceAvailable = false } = service || {};
    const { currentBet } = auction;

    if (checkServiceAction.type === SELECT_PUBLISH_TERMS_SERVICE) {
      const unavailableReason: EUnavailableReason | null = yield select(selectServiceUnavailableReason);

      if (isServiceAvailable) {
        trackServiceClick({
          analyticsOfferStatus,
          analyticsProduct,
          serviceId,
          currentBet,
          offerPosition: offerSerpPosition,
          page: offerSerpPage,
        });
      } else {
        trackUnavailableServiceClick({ analyticsOfferStatus, analyticsProduct, unavailableReason });
      }
    } else if (checkServiceAction.type === CHANGE_AUCTION_BET) {
      if (checkServiceAction.payload.source === 'input') {
        trackBetTyping({
          analyticsOfferStatus,
          analyticsProduct,
          selectedServiceId: serviceId,
          currentBet,
          offerPosition: offerSerpPosition,
          page: offerSerpPage,
        });
      } else {
        // Трекаем событие клика по кнопкам поля ввода ставки
        trackBetChange({
          analyticsOfferStatus,
          analyticsProduct,
          sign: checkServiceAction.payload.source,
          selectedServiceId: serviceId,
          currentBet,
          offerPosition: offerSerpPosition,
          page: offerSerpPage,
        });
      }
    }
  } else {
    yield put(checkServiceFail(response.payload));
  }
}
